import React, { useState } from "react";
import useThemeHook from "../hooks/useThemeHook";
import { THEME_COLORS } from "../themes/DarkLight";
import LoadingIndicator from "./LoadingIndicator";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";
import { Wand2, Play, Crown, Check, AlertTriangle } from "lucide-react";
import { GRANT_AD_BOOSTS } from "../utils/rewardTables";
import { useSelector } from "react-redux";

interface PreGrantAdModalProps {
  closeModal: () => void;
  /** Number of VERIFIED rewarded ads the user watched (0, 1 or 2). */
  onClaim: (adsWatched: number) => void;
  grantAmount: number;
}

/** Grant value for a given number of verified ads, straight from the table. */
const boostedAmount = (grantAmount: number, ads: number) =>
  (grantAmount || 0) * (GRANT_AD_BOOSTS[ads] ?? 1);

const PreGrantAdModal: React.FC<PreGrantAdModalProps> = ({
  closeModal,
  onClaim,
  grantAmount,
}) => {
  const theme = useThemeHook();
  const isDark = theme === "dark";
  const userId = useSelector((state: any) => state?.actualUser?.user?.id) as
    | string
    | undefined;

  /** Verified ads so far — the ONLY thing that decides the multiplier. */
  const [adsWatched, setAdsWatched] = useState(0);
  const [target, setTarget] = useState<number | null>(null);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);

  const claimWith = (ads: number) => {
    if (claimed) return;
    setClaimed(true);
    closeModal();
    onClaim(ads);
  };

  /**
   * Watches ads one at a time until `goal` verified ads are collected.
   * A failed / unverified ad never increases the multiplier: the modal stays
   * open with whatever was actually earned and the user decides what to do.
   */
  const watchAdsUpTo = async (goal: number) => {
    if (isWatchingAd || claimed) return;
    setError(null);
    setTarget(goal);
    setIsWatchingAd(true);

    let earned = adsWatched;
    try {
      while (earned < goal) {
        let rewarded = false;
        // onSuccess only fires for a verified rewarded ad.
        // eslint-disable-next-line no-await-in-loop
        await piNetworkAdsManager.handleRewardedAdFlowFront(userId || "", () => {
          rewarded = true;
        });
        if (!rewarded) break;
        earned += 1;
        setAdsWatched(earned);
      }
    } catch (err) {
      console.error("Error watching ad for boost:", err);
    } finally {
      setIsWatchingAd(false);
      setTarget(null);
    }

    if (earned >= goal) {
      claimWith(earned);
      return;
    }

    setError(
      earned > 0
        ? `Only ${earned} of ${goal} ads was verified. You can still claim at ${GRANT_AD_BOOSTS[earned]}x.`
        : "That ad wasn't completed, so no boost was earned. You can retry or claim now."
    );
  };

  const textColor = THEME_COLORS[theme].inverse_theme;
  const purple = THEME_COLORS[theme].piPurple;

  const tiers = [
    { ads: 0, label: "Now", note: "No ad", color: textColor },
    { ads: 1, label: "1.5x", note: "1 ad", color: purple },
    { ads: 2, label: "2x", note: "2 ads", color: "#f59e0b" },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        gap: "16px",
        textAlign: "center",
      }}
    >
      <div style={{ position: "relative", marginBottom: "6px" }}>
        <div
          style={{
            position: "absolute",
            inset: "-10px",
            background: purple,
            opacity: 0.2,
            filter: "blur(15px)",
            borderRadius: "50%",
          }}
        />
        <Wand2 color={purple} size={50} />
      </div>

      <div>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            color: textColor,
            marginBottom: "6px",
          }}
        >
          Boost your grant
        </h2>
        <p style={{ fontSize: "14px", color: textColor, opacity: 0.7, lineHeight: 1.4 }}>
          Each verified ad multiplies this grant. Claim any time.
        </p>
      </div>

      {/* Honest comparison: every amount below is what the backend credits */}
      <div
        style={{
          backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
          borderRadius: "16px",
          padding: "12px",
          width: "100%",
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {tiers.map((tier, i) => (
          <React.Fragment key={tier.ads}>
            {i > 0 && (
              <div
                style={{
                  height: "40px",
                  width: "1px",
                  backgroundColor: textColor,
                  opacity: 0.1,
                }}
              />
            )}
            <div style={{ textAlign: "center", flex: 1 }}>
              <p
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                  color: tier.color,
                  opacity: tier.ads === 0 ? 0.5 : 1,
                }}
              >
                {tier.label}
              </p>
              <p style={{ fontWeight: "bold", fontSize: "16px", color: tier.color }}>
                {boostedAmount(grantAmount, tier.ads).toFixed(2)}
              </p>
              <p style={{ fontSize: "9px", color: tier.color, opacity: 0.6, marginTop: "2px" }}>
                {tier.note}
              </p>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Progress of verified ads */}
      {adsWatched > 0 && (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            fontSize: "12px",
            fontWeight: 700,
            color: purple,
          }}
        >
          <Check size={14} />
          <span>
            {adsWatched} verified ad{adsWatched > 1 ? "s" : ""} · currently{" "}
            {GRANT_AD_BOOSTS[adsWatched]}x
          </span>
        </div>
      )}

      {error && (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            padding: "10px 12px",
            borderRadius: "12px",
            backgroundColor: "rgba(245, 158, 11, 0.12)",
            border: "1px solid rgba(245, 158, 11, 0.35)",
            color: "#b45309",
            fontSize: "12px",
            textAlign: "left",
          }}
        >
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: "1px" }} />
          <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
        {isWatchingAd ? (
          <div style={{ padding: "12px" }}>
            <LoadingIndicator />
            <p style={{ fontSize: "12px", color: textColor, opacity: 0.6, marginTop: "8px" }}>
              {target === 2 ? `Ad ${Math.min(adsWatched + 1, 2)} of 2…` : "Loading ad…"}
            </p>
          </div>
        ) : (
          <>
            {adsWatched < 2 && (
              <button
                onClick={() => watchAdsUpTo(2)}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "14px",
                  border: "2px solid #f59e0b",
                  background: "linear-gradient(135deg, #f59e0b, #d97706)",
                  color: "white",
                  fontWeight: 800,
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Crown size={18} />
                {adsWatched === 1
                  ? `Watch 1 more ad → 2x (${boostedAmount(grantAmount, 2).toFixed(2)} ATH)`
                  : `Watch 2 ads → 2x (${boostedAmount(grantAmount, 2).toFixed(2)} ATH)`}
              </button>
            )}

            {adsWatched < 1 && (
              <button
                onClick={() => watchAdsUpTo(1)}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "14px",
                  border: "none",
                  background: purple,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Play size={18} />
                Watch 1 ad → 1.5x ({boostedAmount(grantAmount, 1).toFixed(2)} ATH)
              </button>
            )}

            <button
              onClick={() => claimWith(adsWatched)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "14px",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)"}`,
                background: "transparent",
                color: textColor,
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              {adsWatched === 0
                ? `Claim now (1x) — ${boostedAmount(grantAmount, 0).toFixed(2)} ATH`
                : `Claim at ${GRANT_AD_BOOSTS[adsWatched]}x — ${boostedAmount(
                    grantAmount,
                    adsWatched
                  ).toFixed(2)} ATH`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PreGrantAdModal;
